import { useEffect, useState } from "react";
import { Card, Row, Col, Button, Spinner, Alert } from "react-bootstrap";
import api from "@/api/api.js";

const fmt = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

const safe = (v) => (v === null || v === undefined || v === "" ? "-" : v);

const ReceptorDetail = ({ contactId, canDownload = true, statusOnly = false }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    let active = true;
    setLoading(true);
    api
      .get(`/contacts/${contactId}`)
      .then(({ data }) => active && setContact(data))
      .catch(() => active && setContact(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [contactId]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const { data } = await api.get(
        `/contacts/${contactId}/downloadCertificateTax`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = contact.certificateTaxOriginalName || "constancia.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      /* noop */
    } finally {
      setDownloading(false);
    }
  };

  const Header = (
    <Card.Header>Receptor (se factura a)</Card.Header>
  );

  if (loading) {
    return (
      <Card className="h-100">
        {Header}
        <Card.Body className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span>Cargando receptor...</span>
        </Card.Body>
      </Card>
    );
  }

  if (!contact) {
    return (
      <Card className="h-100">
        {Header}
        <Card.Body>
          <Alert variant="warning" className="mb-0">
            No se pudo cargar la información del receptor.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const address = [
    contact.street,
    contact.exterior && `#${contact.exterior}`,
    contact.interior && `Int. ${contact.interior}`,
    contact.suburb,
    contact.town,
    contact.state,
    contact.postalCode && `C.P. ${contact.postalCode}`,
  ]
    .filter(Boolean)
    .join(", ");

  const hasCert = !!contact.certificateTaxOriginalName;

  return (
    <Card className="h-100">
      {Header}
      <Card.Body>
        <Row className="g-2">
          <Col xs={12} md={6}>
            <strong>Razón social:</strong> {safe(contact.businessName)}
          </Col>
          <Col xs={12} md={6}>
            <strong>RFC:</strong> {safe(contact.taxIdentification)}
          </Col>
          <Col xs={12} md={6}>
            <strong>Tipo de persona:</strong> {safe(contact.type)}
          </Col>
          <Col xs={12} md={6}>
            <strong>Teléfono:</strong> {safe(contact.phone)}
          </Col>
          <Col xs={12} md={6}>
            <strong>Correo:</strong> {safe(contact.emailContact)}
          </Col>
          <Col xs={12}>
            <strong>Domicilio fiscal:</strong> {safe(address)}
          </Col>
        </Row>

        <hr className="my-2" />
        <div className="fw-bold mb-1">Constancia de situación fiscal</div>
        {statusOnly ? (
          <span className={`badge bg-${hasCert ? "success" : "secondary"}`}>
            Constancia del receptor: {hasCert ? "Sí" : "No"}
          </span>
        ) : hasCert ? (
          <>
            <div>
              <strong>Archivo:</strong>{" "}
              {safe(contact.certificateTaxOriginalName)}
            </div>
            <div>
              <strong>Subida:</strong>{" "}
              {fmt(contact.certificateTaxLastUploadDate)}
            </div>
            <div>
              <strong>Vigente hasta:</strong>{" "}
              {fmt(contact.certificateTaxValidDate)}
            </div>
            {canDownload ? (
              <Button
                size="sm"
                variant="outline-success"
                className="mt-2"
                onClick={handleDownload}
                disabled={downloading}
              >
                <i className="bi bi-download me-1" />
                {downloading
                  ? "Descargando..."
                  : "Descargar constancia del receptor"}
              </Button>
            ) : (
              <div className="text-muted mt-2" style={{ fontSize: 13 }}>
                Disponible solo para el contador que está trabajando la factura.
              </div>
            )}
          </>
        ) : (
          <span className="text-muted">
            Sin constancia de situación fiscal registrada.
          </span>
        )}
      </Card.Body>
    </Card>
  );
};

export default ReceptorDetail;
